<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { AppState } from '../../etc/stores';
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import ChevronLeft from '../icons/ChevronLeft.svelte';
  import InfoPostCheckout from '../icons/InfoPostCheckout.svelte';
  import Heading from '../typography/Heading.svelte';
  import Label from '../typography/Label.svelte';
  import Text from '../typography/Text.svelte';
  import { formatMoney, isMobile } from '../../etc/utils';
  import PostcheckoutHowItWorksForm from './PostcheckoutHowItWorksForm.svelte';
  import { replaceVariables } from '../../i18n';
  import Dashed from '../icons/Dashed.svelte';
  import flags from '../../etc/flagKeys';

  const t = (text: string, values?: Record<string, string | number>) => {
    return replaceVariables($_(`postcheckout_how_it_works.${text}`), values);
  };

  const dispatch = createEventDispatcher();

  export let email = '';
  export let error = '';
  export let loading = false;
  let tab = 0;

  const changeTab = (newTab: number) => {
    tab = newTab;
  };

  const getLoyaltyMoney = () => {
    const { location, rewardsSettings } = $AppState.business;
    return formatMoney(
      location,
      100 / Number(rewardsSettings.coinRewardPercentage)
    );
  };
</script>

<div class="twism-postcheckout-how">
  <div class="twism-postcheckout-how--back" on:click={() => dispatch('back')}>
    <ChevronLeft size={isMobile() ? 16 : 12} />
    <Text class="twism-postcheckout-how--back--text">{t('back')}</Text>
  </div>
  <div class="twism-postcheckout-how--title">
    <Heading type="h3">{t('title')}</Heading>
  </div>
  <div
    class="twism-postcheckout-how--mobile-tabs"
    style="background: {$AppState.theme.colors.typography}0D"
  >
    <div
      class="twism-postcheckout-how--mobile-tabs--item {tab === 0
        ? '-twism-tab-active'
        : ''}"
      on:click={() => changeTab(0)}
    >
      <Label type="medium-regular-semibold">
        {t('tab_earning.tab_title')}
      </Label>
    </div>
    <div
      class="twism-postcheckout-how--mobile-tabs--item {tab === 1
        ? '-twism-tab-active'
        : ''}"
      on:click={() => changeTab(1)}
    >
      <Label type="medium-regular-semibold">
        {t('tab_using.tab_title')}
      </Label>
    </div>
  </div>
  <div class="twism-postcheckout-how--tabs">
    <div on:click={() => changeTab(0)}>
      <Text
        class="twism-postcheckout-how--tab {tab === 0
          ? '-twism-tab-active'
          : ''}"
      >
        {t('tab_earning.tab_title')}
      </Text>
    </div>
    <div on:click={() => changeTab(1)}>
      <Text
        class="twism-postcheckout-how--tab {tab === 1
          ? '-twism-tab-active'
          : ''}"
      >
        {t('tab_using.tab_title')}
      </Text>
    </div>
  </div>
  <div class="twism-postcheckout-how--content">
    {#if tab === 0}
      <div class="twism-postcheckout-how--content--wrapper" transition:fade>
        <Heading class="twism-postcheckout-how--content--title">
          {t('tab_earning.title')}
        </Heading>
        <div class="twism-postcheckout-how--content--box">
          <div class="twism-postcheckout-how--content--box--icon">
            <InfoPostCheckout />
          </div>
          <div class="twism-postcheckout-how--content--box--texts">
            <Label type="medium-regular-semibold" component="h3"
              >{t('tab_earning.loyalty.title', {
                loyalty_money: getLoyaltyMoney()
              })}</Label
            >
            <Text type="medium">{t('tab_earning.loyalty.description')}</Text>
          </div>
        </div>
        <Heading type="h3" component="h2">{t('tab_earning.subtitle')}</Heading>
        <Text type="medium">
          {t('tab_earning.description', {
            currency: formatMoney($AppState.business.location, 1)
          })}
        </Text>
        <Dashed class="twism-postcheckout-how--content--divisor" />
        <Heading type="h3" component="h2">
          {t('tab_earning.ways_earn_coin.title')}
        </Heading>
        <ol>
          <li>
            <strong>1.</strong>
            <Text type="medium">
              {t('tab_earning.ways_earn_coin.step-1')}
            </Text>
          </li>
          <li>
            <strong>2.</strong>
            <Text type="medium">
              {t('tab_earning.ways_earn_coin.step-2')}
            </Text>
          </li>
          <li>
            <strong>3.</strong>
            <Text type="medium">
              {t('tab_earning.ways_earn_coin.step-3')}
            </Text>
          </li>
          <li>
            <strong>4.</strong>
            <Text type="medium">
              {t('tab_earning.ways_earn_coin.step-4')}
            </Text>
          </li>
        </ol>
        <PostcheckoutHowItWorksForm
          {email}
          {error}
          {loading}
          on:changeEmail={e => dispatch('changeEmail', e.detail)}
          on:submit={() => dispatch('submit')}
          on:signin={() => dispatch('signin')}
        />
      </div>
    {:else}
      <div class="twism-postcheckout-how--content--wrapper" transition:fade>
        <Heading class="twism-postcheckout-how--content--title">
          {t('tab_using.title')}
        </Heading>
        <Heading type="h3" component="h2">{t('tab_using.subtitle')}</Heading>
        <Text type="medium">{t('tab_using.description')}</Text>
        <Dashed class="twism-postcheckout-how--content--divisor" />
        <Heading type="h3" component="h2"
          >{t('tab_using.ways_use_coin.title')}</Heading
        >
        <ol>
          <li>
            <strong>1.</strong><Text type="medium"
              >{t('tab_using.ways_use_coin.step-1')}</Text
            >
          </li>
          <li>
            <strong>2.</strong><Text type="medium"
              >{t('tab_using.ways_use_coin.step-2')}</Text
            >
          </li>
        </ol>
        <PostcheckoutHowItWorksForm
          {email}
          {error}
          {loading}
          on:changeEmail={e => dispatch('changeEmail', e.detail)}
          on:submit={() => dispatch('submit')}
          on:signin={() => dispatch('signin')}
        />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .twism-postcheckout-how {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    padding: 68px 0 0 48px;
    box-sizing: border-box;
    overflow-y: hidden;
    height: 100%;

    @media screen and (max-width: 468px) {
      padding: 128px 0 0 24px;
    }

    &--title {
      display: none;
      position: absolute;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);

      @media screen and (max-width: 468px) {
        display: block;
      }
    }

    &--back {
      position: absolute;
      top: 16px;
      left: 16px;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.3s ease-in-out;

      &:hover {
        opacity: 1;
      }

      @media screen and (max-width: 468px) {
        opacity: 0.6 !important;
      }
    }

    &--mobile-tabs {
      display: none;
      width: calc(100% - 40px);
      height: 56px;
      border-radius: 100px;
      padding: 8px;
      box-sizing: border-box;
      position: absolute;
      top: 72px;
      left: 50%;
      transform: translateX(-50%);
      justify-content: space-between;

      &--item {
        width: calc(50% - 8px);
        height: 100%;
        border-radius: 100px;
        display: flex;
        justify-content: center;
        align-items: center;

        &.-twism-tab-active {
          background: #ffffff;
          box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.05);
        }
      }

      @media screen and (max-width: 468px) {
        display: flex;
      }
    }

    &--tabs {
      width: 196px;
      height: 100%;
      padding-right: 65px;
      box-sizing: border-box;

      @media screen and (max-width: 468px) {
        display: none;
      }
    }

    &--content {
      flex: 1;
      height: 100%;
      position: relative;
      padding-top: 12px;

      ol {
        margin-bottom: 48px;
      }

      li {
        margin-bottom: 16px;
        display: flex;

        strong {
          font-weight: 600;
          font-size: 14px;
          line-height: 150%;
          color: var(--twism-theme-primary100);
          margin-right: 4px;
        }
      }

      &--wrapper {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 12px;
        overflow: scroll;
        padding-bottom: 24px;
        padding-right: 88px;
        box-sizing: border-box;

        @media screen and (max-width: 468px) {
          padding-right: 24px;
          padding-top: 40px;
        }
      }

      &--box {
        width: 100%;
        box-sizing: border-box;
        border-radius: 8px;
        background: var(--twism-theme-primary600);
        padding: 18px 16px 16px 18px;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        margin-bottom: 40px;

        &--icon {
          min-width: 22px;
        }

        &--texts {
          margin-left: 12px;
        }
      }
    }
  }

  :global(.twism-postcheckout-how--content h3) {
    margin-bottom: 8px;

    @media screen and (max-width: 468px) {
      font-size: 14px;
      line-height: 20px;
    }
  }

  :global(.twism-postcheckout-how--content--divisor) {
    margin: 40px 0;
  }

  :global(.twism-postcheckout-how--content h2) {
    margin-bottom: 16px;

    @media screen and (max-width: 468px) {
      font-size: 18px;
      line-height: 25px;
    }
  }

  :global(.twism-postcheckout-how--content--title) {
    font-size: 36px !important;
    line-height: 44px !important;
    letter-spacing: -0.005em !important;
    margin-bottom: 32px !important;

    @media screen and (max-width: 468px) {
      font-size: 24px !important;
      line-height: 29px !important;
    }
  }

  :global(.twism-postcheckout-how--back--text) {
    font-weight: 500 !important;
    margin-left: 6px !important;

    @media screen and (max-width: 468px) {
      display: none;
    }
  }

  :global(.twism-postcheckout-how--content--box--text) {
    font-weight: 500;
    text-align: center !important;
  }

  :global(.twism-postcheckout-how--mobile-tabs--item.-twism-tab-active p) {
    color: var(--twism-theme-primary200) !important;
  }

  :global(.twism-postcheckout-how--tab) {
    opacity: 0.6;
    cursor: pointer;
    padding: 12px !important;
  }
  :global(.twism-postcheckout-how--tab.-twism-tab-active) {
    opacity: 1;
    font-weight: 500;
  }
</style>
